// ========== MAPPING MODELS ==========
// After an extraction process completes, the backend dispatches a mapping job.
// Mapping progress is delivered over a single org-wide WebSocket (separate
// from the per-process extraction socket); each message carries the extraction
// `process_id` / `process_item_id` so the UI can correlate a job to the result
// that triggered it.

export const MappingWebSocketMessageType = {
  Authenticate: "authenticate",
  Authenticated: "authenticated",
  AuthFailed: "auth_failed",
  Ping: "ping",
  Pong: "pong",
  Progress: "mapping.job.progress",
  Completed: "mapping.job.completed",
  Failed: "mapping.job.failed",
} as const;

export type MappingWebSocketMessageType =
  (typeof MappingWebSocketMessageType)[keyof typeof MappingWebSocketMessageType];

// Raw message shape as it arrives on the wire from the mapping notifier.
export interface IMappingNotificationMessage {
  type: string;
  job_id?: string | null;
  plugin_id?: string | null;
  service?: string | null;
  status?: string | null;
  rows_total?: number | null;
  rows_done?: number | null;
  rows_ok?: number | null;
  rows_failed?: number | null;
  result_url_path?: string | null;
  process_id?: string | null;
  process_item_id?: string | null;
  // Present on auth / error frames.
  message?: string;
  org_id?: string;
  user_id?: string;
}

// Backend job status as returned by the mapping REST API. These are the
// raw lifecycle states; the UI collapses them into `MappingViewStatus`.
export const MappingJobBackendStatus = {
  AwaitingUpload: "AwaitingUpload",
  Parsing: "Parsing",
  Running: "Running",
  Completed: "Completed",
  Failed: "Failed",
} as const;

export type MappingJobBackendStatus =
  (typeof MappingJobBackendStatus)[keyof typeof MappingJobBackendStatus];

// Raw job-status shape from GET /jobs?process_item_id=... (404 when no job
// has been dispatched yet for the extraction result item).
export interface IMappingJobStatusResponse {
  job_id: string;
  status: string;
  rows_total?: number | null;
  rows_done?: number | null;
  rows_failed?: number | null;
  accepted_count?: number | null;
  plugin_id?: string | null;
  service?: string | null;
  error?: string | null;
}

// UI-facing status for a mapping job attached to an extraction result item.
export const MappingViewStatus = {
  Running: "running",
  Completed: "completed",
  Failed: "failed",
} as const;

export type MappingViewStatus =
  (typeof MappingViewStatus)[keyof typeof MappingViewStatus];

// Per-item mapping state the UI renders inside the extracted result.
export interface IMappingJobViewModel {
  jobId: string;
  processId: string;
  processItemId: string;
  status: MappingViewStatus;
  rowsDone: number;
  rowsTotal: number;
  service?: string | null;
  pluginId?: string | null;
}

// Response from POST /processes/{id}/items/{itemId}/dispatch-mapping — the
// on-demand "Run mapping" trigger. `status` is "DISPATCHED" or
// "ALREADY_DISPATCHED"; the job itself is created asynchronously downstream.
export interface IMappingDispatchResponse {
  status: string;
  process_id: string;
  process_item_id: string;
}
